import React from 'react';
import {
  Show,
  ShowProps,
  useShowController,
  SimpleShowLayout,
  FunctionField,
  TextField,
  FileField,
  useGetIdentity,
  useTranslate,
} from 'react-admin';

import { Divider } from '@material-ui/core';
import RecommendedActionsConfigurator from '../../../components/RecommendedActionsConfigurator';
import SectionTitle from '../../../components/SectionTitle';
import TestHistory from '../../../components/TestHistory';
import TestResults from '../../../components/TestResults';
import { TestNames } from '../../../constants/test/testNames';
import { PartnerNames } from '../../../constants/partner/partnerNames';

import './styles.scss';

const calculateAge = (dateOfBirth: string) => {
  const date = new Date(dateOfBirth);
  const diff = Date.now() - date.getTime();
  const ageDate = new Date(diff);
  return Math.abs(ageDate.getUTCFullYear() - 1970);
};

const FertilityCheckShow = (props: ShowProps) => {
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const { record, error, loading } = useShowController(props);
  const isAdmin = identity?.userRoles.includes('admin');

  if (loading) {
    return <div>Loading</div>;
  }

  if (error) {
    return <div>Error {error}</div>;
  }

  return (
    <Show {...props} className="testShow">
      <SimpleShowLayout>
        <SectionTitle title={translate('testResults.userData')} />
        <TextField
          className="fertilityCheckUserInfo text"
          source="user.fullName"
          label="testResults.fullName"
          emptyText="-"
        />

        <TextField
          className="fertilityCheckUserInfo text"
          source="user.formattedDateOfBirth"
          label="testResults.dateOfBirth"
          emptyText="-"
        />
        <FunctionField
          className="fertilityCheckUserInfo text"
          render={() => calculateAge(record?.user.dateOfBirth)}
          label="testResults.age"
        />
        <TextField
          className="fertilityCheckUserInfo text"
          source="user.lang"
          label="testResults.userLanguage"
          emptyText="DE"
        />

        <TextField
          className="fertilityCheckUserInfo text"
          source="user.phoneNumber"
          label="testResults.phoneNumber"
          emptyText="-"
        />

        <TextField
          className="fertilityCheckUserInfo text"
          source="user.emailAddress"
          label="testResults.email"
          emptyText="-"
        />

        {isAdmin && record?.partnerCode && (
          <FunctionField
            className="fertilityCheckUserInfo text"
            render={() =>
              PartnerNames[record?.partnerCode as keyof typeof PartnerNames]
            }
            label="testResults.partner"
            emptyText="-"
          />
        )}
        <Divider className="divider" />
        <TextField
          className="fertilityCheckUserInfo text"
          source="answers.femaleMedicalHistory"
          label="testResults.medicalHistory"
          emptyText="-"
        />
        <TextField
          className="fertilityCheckUserInfo text"
          source="answers.fertilityCheckSymptoms"
          label="testResults.symptoms"
          emptyText="-"
        />
        <TextField
          className="fertilityCheckUserInfo text"
          source="answers.menstrualCycle"
          label="testResults.menstrualCycle"
          emptyText="-"
        />
        <TextField
          className="fertilityCheckUserInfo text"
          source="answers.whenLastPeriodStarted"
          label="testResults.whenLastPeriodStarted"
          emptyText="-"
        />
        <TextField
          className="fertilityCheckUserInfo text"
          source="answers.pregnantBefore"
          label="testResults.pregnantBefore"
          emptyText="-"
        />
        {record?.answers?.pregnancyOutput && (
          <TextField
            className="fertilityCheckUserInfo text"
            source="answers.pregnancyOutput"
            label="testResults.pregnancyOutput"
            emptyText="-"
          />
        )}
        {record?.answers?.breastFeedingNow && (
          <TextField
            className="fertilityCheckUserInfo text"
            source="answers.breastFeedingNow"
            label="testResults.breastFeedingNow"
          />
        )}
        <Divider className="divider" />
        <TestHistory record={record} />
        <FileField
          target="_blank"
          className="text--purple"
          source="labReport"
          title={translate('testResults.openLabReport')}
          label="testResults.labReport"
        />
        <FunctionField
          className="text"
          render={() => TestNames[record?.type as keyof typeof TestNames]}
          label="testResults.testType"
        />
        {record && (
          <TestResults
            data={record.results.tests}
            partnerCode={record.partnerCode}
            userInfo={record.user}
          />
        )}
        <Divider className="divider" />
        <RecommendedActionsConfigurator record={record} readOnly />
      </SimpleShowLayout>
    </Show>
  );
};

export default FertilityCheckShow;
